import { createApi } from '@reduxjs/toolkit/query/react';
import { Language } from '@shared/enum';
import { apiBaseQuery } from '@shared/functions';
import { type ErrorObject } from '@state/userApi';

export interface GroupItem {
    group_id: number;
    name: string;
    description: string | null;
    user_count: number;
}

export interface ListGroupsRequest {
    language: Language;
    page: number;
    pageSize: number;
    search?: string;
}

export interface ListGroupsResponse {
    status: number;
    message: string;
    data: {
        groups: GroupItem[];
        total_count: number;
        page: number;
        page_size: number;
    };
    error: ErrorObject;
}

export interface RemoveGroupFromUIRequest {
    language: Language;
    group_id: number;
}

export interface RemoveGroupFromUIResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface CreateGroupRequest {
    language: Language;
    name: string;
    description?: string;
    user_ids: number[];
}

export interface CreateGroupResponse {
    status: number;
    message: string;
    data: {
        group_id: number;
    };
    error: ErrorObject;
}

export interface GroupMemberUser {
    user_id: number;
    first_name: string;
    last_name: string;
    login: string;
    driver_license: string | null;
}

export interface GetGroupRequest {
    language: Language;
    group_id: number;
}

export interface GetGroupResponse {
    status: number;
    message: string;
    data: {
        group_id: number;
        name: string;
        description: string | null;
        is_in_assignments: boolean;
        users: GroupMemberUser[];
    };
    error: ErrorObject;
}

export interface UpdateGroupRequest {
    language: Language;
    group_id: number;
    name: string;
    description?: string;
    user_ids: number[];
}

export interface UpdateGroupResponse {
    status: number;
    message: string;
    data: {
        group_id: number;
    };
    error: ErrorObject;
}

export const groupApi = createApi({
    reducerPath: 'groupApi',
    baseQuery: apiBaseQuery,
    tagTypes: ['Groups'],
    endpoints: (builder) => ({
        listGroups: builder.query<ListGroupsResponse, ListGroupsRequest>({
            query: ({ language, page, pageSize, search }) => ({
                url: `v1/group/list_groups?language=${encodeURIComponent(language.toLowerCase())}&page=${page}&pageSize=${pageSize}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['Groups'],
        }),
        removeGroup: builder.mutation<RemoveGroupFromUIResponse, RemoveGroupFromUIRequest>({
            query: (data) => ({
                url: 'v1/group/remove_from_ui',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    group_id: data.group_id,
                },
            }),
            invalidatesTags: ['Groups'],
        }),
        createGroup: builder.mutation<CreateGroupResponse, CreateGroupRequest>({
            query: (data) => ({
                url: 'v1/group/create_group',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    name: data.name,
                    description: data.description ?? '',
                    user_ids: data.user_ids,
                },
            }),
            invalidatesTags: ['Groups'],
        }),
        getGroup: builder.query<GetGroupResponse, GetGroupRequest>({
            query: ({ language, group_id }) => ({
                url: `v1/group/get_group?language=${encodeURIComponent(language.toLowerCase())}&group_id=${group_id}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: (_result, _error, arg) => [{ type: 'Groups', id: arg.group_id }],
        }),
        updateGroup: builder.mutation<UpdateGroupResponse, UpdateGroupRequest>({
            query: (data) => ({
                url: 'v1/group/update_group',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    group_id: data.group_id,
                    name: data.name,
                    description: data.description ?? '',
                    user_ids: data.user_ids,
                },
            }),
            invalidatesTags: ['Groups'],
        }),
    }),
});

export const {
    useListGroupsQuery,
    useRemoveGroupMutation,
    useCreateGroupMutation,
    useGetGroupQuery,
    useUpdateGroupMutation,
} = groupApi;
